import { Injectable } from '@angular/core';
import { createStore, select, setProps, withProps } from '@ngneat/elf';
import { getAllEntities, getEntity, setEntities, upsertEntities, withEntities } from '@ngneat/elf-entities';
import { persistState } from '@ngneat/elf-persist-state';
import {
  clearRequestsCache,
  createRequestDataSource,
  createRequestsCacheOperator,
  createRequestsStatusOperator,
  updateRequestCache,
  withRequestsCache,
  withRequestsStatus,
} from '@ngneat/elf-requests';
import { RestaurantAnimationDto } from '@timechef/core/api';
import { capacitorStorageStrategy, includeKeys } from '@timechef/core/utils/elf-utils';
import { minutesToMilliseconds } from 'date-fns';
import { map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PageInfo } from './animations.models';
import { computeAnimations } from './animations.utils';

export interface AnimationsProps {
  animationsPageInfo: PageInfo;
  shownIds: number[];
}

export enum AnimationStatusKey {
  Animation = 'animations',
}

@Injectable({ providedIn: 'root' })
export class AnimationsRepository {
  private store = createStore(
    { name: 'animation' },
    withProps<AnimationsProps>({
      animationsPageInfo: {
        hasMore: false,
        currentPageIndex: 0,
      },
      shownIds: [],
    }),
    withEntities<RestaurantAnimationDto>(),
    withRequestsStatus<AnimationStatusKey>(),
    withRequestsCache<`animations-${string}`>(),
  );

  instance = persistState(this.store, {
    key: 'animation',
    storage: capacitorStorageStrategy,
    source: () => this.store.pipe(includeKeys(['entities', 'ids', 'requestsCache', 'requestsStatus', 'shownIds'])),
  });

  animationsByPeriodDS = createRequestDataSource({
    store: this.store,
    data$: () =>
      this.store.pipe(
        select((s) => s.shownIds.map((id) => s.entities[id])),
        map((animations) => computeAnimations(new Date(), animations)),
      ),
    requestKey: AnimationStatusKey.Animation,
    dataKey: 'data',
    idleAsPending: true,
  });

  animationsPageInfo$ = this.store.pipe(select((s) => s.animationsPageInfo));

  getAnimationById = (id: number) => this.store.query(getEntity(id));
  getAnimationsRequestStatus = () => this.store.query((s) => s.requestsStatus[AnimationStatusKey.Animation]);

  addAnimations(animations: RestaurantAnimationDto[], hasMore: boolean, currentPageIndex: number, animationInRestaurantIds: number[]) {
    const clear = currentPageIndex === 0;

    // On garde les animations présentes dans les restaurants, sauf les nouvelles qu'on va ajouter et qui seront à jour
    let newAnimations: RestaurantAnimationDto[] = [];

    if (clear) {
      const animationsToKeepFromStoreIds = animationInRestaurantIds.filter((x) => !animations.map((x) => x.id).includes(x));

      newAnimations = this.store
        .query(getAllEntities())
        .filter((a) => animationsToKeepFromStoreIds.includes(a.id))
        .concat(animations);
    }

    animations
      ? this.store.update(
          updateRequestCache(`animations-${currentPageIndex}`, { ttl: minutesToMilliseconds(environment.cache.defaultMinutes) }),
          setProps({ animationsPageInfo: { hasMore, currentPageIndex } }),
          clear ? setEntities(newAnimations) : upsertEntities(animations),
          (s) => ({ ...s, shownIds: clear ? animations.map((a) => a.id) : [...s.shownIds, ...animations.map((a) => a.id)] }),
          this.animationsByPeriodDS.setSuccess(),
        )
      : this.store.update(
          updateRequestCache(`animations-${currentPageIndex}`, { ttl: minutesToMilliseconds(environment.cache.defaultMinutes) }),
          setProps({ animationsPageInfo: { hasMore, currentPageIndex } }),
          this.animationsByPeriodDS.setSuccess(),
        );
  }

  addAnimationsFromRestaurants(animations: RestaurantAnimationDto[]) {
    this.store.update(upsertEntities(animations));
  }

  clearRequestCache() {
    this.store.update(clearRequestsCache());
  }

  public trackRequestStatus = createRequestsStatusOperator(this.store);
  public skipWhileAnimationsCached = createRequestsCacheOperator(this.store);
}
